import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState,
} from "react";

import { apiFetch } from "../apiBindings";
import { PageContext } from "../components/shared/Page";
import {
    storageLocal as storage,
    storageSession as session,
} from "../utils/StorageHandler";

export const DataContext = createContext();

const HeatingOilDataProvider = ({ children }) => {
    const pageContext = useContext(PageContext);
    const { updateLastCalculator, setHelZipcode } = pageContext.actions;

    /**
     * Zipcode
     */
    const [zipcode, setZipcode] = useState(storage.get("hel:zipcode", ""));

    /**
     * Amount
     */
    const [amount, setAmount] = useState(storage.getInt("hel:amount", 3000));

    /**
     * Up
     */
    const [up, setUp] = useState(storage.getInt("hel:up", 1));
    /**
     * Result with prices
     */
    const [result, setResult] = useState([]);
    /**
     * Settings for the price additions
     */
    const [additions, setAdditions] = useState(null);

    /**
     * Loading state
     */
    const [loading, setLoading] = useState(false);
    const [loadingError, setLoadingError] = useState(false);

    /**
     * Chosen product
     */
    const [productChoice, setProductChoice] = useState(
        storage.getInt("hel:product-choice", 1)
    );

    const [zipcodeIsValid, setZipcodeIsValid] = useState(true);
    const [checkingForZipcode, setCheckingForZipcode] = useState(false);
    const [checkedZipcode, setCheckedZipcode] = useState(zipcode);

    const [amountIsValid, setAmountIsValid] = useState(true);
    const [checkingForAmount, setCheckingForAmount] = useState(false);

    const [shouldCalculate, setShouldCalculate] = useState(false);

    useEffect(() => {
        startup();
    }, []);

    /**
     * Fetch the prices from the api and store them into the result.
     */
    const calculate = useCallback(
        async (localZipcode, localAmount, localUp) => {
            updateLastCalculator("heizoel");

            if (!loading) {
                // do the calculation
                setLoading(true);
                apiFetch(
                    `/heatingoil/${localZipcode}/${localAmount}/${localUp}`
                )
                    .then((response) => response.json())
                    .then((data) => {
                        setCheckedZipcode(localZipcode);
                        if (data.status && data.status === "error") {
                            setResult([]);
                            setAdditions(null);
                            setLoadingError(true);
                            setZipcodeIsValid(false);
                        } else {
                            setResult(data.products);
                            setAdditions(data.settings);
                        }

                        setLoading(false);
                    })
                    .catch(() => {
                        setLoading(false);
                        setLoadingError(true);
                    });
            } else {
                // Still loading, do nothing
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    /**
     * Sync zipcode with local storage.
     */
    useEffect(() => {
        storage.set("hel:zipcode", zipcode);
        setHelZipcode(zipcode);
        if (shouldCalculate && zipcode && amount && up) {
            calculate(zipcode, amount, up);
        }
    }, [zipcode, shouldCalculate, setHelZipcode, amount, up, calculate]);

    /**
     * Sync amount with local storage.
     */
    useEffect(() => {
        storage.set("hel:amount", amount);
    }, [amount]);

    /**
     * Sync up with local storage.
     */
    useEffect(() => {
        storage.set("hel:up", up);
    }, [up]);

    /**
     * Sync product choice with local storage.
     */
    useEffect(() => {
        storage.set("hel:product-choice", productChoice);
    }, [productChoice]);

    /**
     * Sync the product infos into the local storage.
     */
    const syncProductInfos = (chosenProduct) => {
        if (chosenProduct) {
            storage.set("hel:product-is-premium", chosenProduct.isPremium);
            storage.set("hel:product-id", chosenProduct.id);
            storage.set("hel:delivery-time", chosenProduct.deliveryTimeType);
            storage.set("hel:climate-neutral", chosenProduct.isClimateNeutral);
        } else {
            storage.set("hel:product-is-premium", false);
        }
    };
    /**
     * Startup call.
     */
    const startup = async () => {
        // Here we can do some startup stuff.
    };

    /**
     * Clear the checkout before we go in the order again.
     */
    const clearCheckout = () => {
        storage.remove("hel:delivery-time-type");
        storage.remove("hel:delivery-time-frame");
        storage.remove("hel:delivery-time-wish-date");
        storage.remove("hel:additional-addresses", []);
        storage.remove("hel:anti-freeze");
        storage.remove("hel:order-comment");
        storage.remove("hel:selected-city");
        storage.remove("hel:selected-street");

        session.remove("hel:checkout");
        session.remove("hel:order");
    };

    /**
     * Checks if the zipcode is valid
     * @param {*} localZipcode
     */
    const checkZipcode = async (localZipcode) => {
        if (localZipcode.length < 5) {
            return;
        }
        setCheckingForZipcode(true);
        setCheckedZipcode(localZipcode);

        const repsonse = await apiFetch(
            `/heatingoil/check-zipcode/${localZipcode}`
        );
        const data = await repsonse.json();

        setZipcodeIsValid(data.is_valid);
        setCheckingForZipcode(false);
    };

    /**
     * Checks if the amount for the given zipcode is valid.
     * @param {*} localZipcode
     * @param {*} localAmount
     */
    const checkAmount = async (localZipcode, localAmount) => {
        if (!localZipcode || !localAmount) {
            return;
        }
        setCheckingForAmount(true);

        const repsonse = await apiFetch(
            `/heatingoil/check-amount/${localZipcode}/${localAmount}`
        );
        const data = await repsonse.json();

        setAmountIsValid(data.is_valid);
        setCheckingForZipcode(false);
    };

    /**
     * Returns the result object for the selected product.
     */
    const getProductWithPrices = () => {
        return result[productChoice];
    };

    const data = {
        zipcode,
        amount,
        up,
        result,
        loading,
        productChoice,
        additions,
        zipcodeIsValid,
        checkedZipcode,
        checkingForZipcode,
        amountIsValid,
        checkingForAmount,
        loadingError,
    };

    const actions = {
        setZipcode,
        setAmount,
        setUp,
        setProductChoice,
        calculate,
        getProductWithPrices,
        clearCheckout,
        checkZipcode,
        checkAmount,
        syncProductInfos,
        setResult,
        setAdditions,
        setShouldCalculate,
    };

    return (
        <DataContext.Provider
            value={{
                data,
                actions,
            }}
        >
            {children}
        </DataContext.Provider>
    );
};

export default HeatingOilDataProvider;
